import { Fragment, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchForm, fetchSubmissions, setForm as setGlobalForm } from '../store/form.slice'
import { NavBack } from '../components/nav-back'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { SubmissionsTab } from '../components/tabs/submissions-tab'
import { QuestionsTab } from '../components/tabs/questions-tab'
import { capitalize, getDirtySubmissionForm } from '../utils/functions'
import { QuestionSchema } from '../utils/types/form'
import { SubmissionSchema } from '../utils/types/submission'

export const AnalysisView = () => {
  const [submissions, setSubmissions] = useState<SubmissionSchema[]>([])
  const [allQuestions, setAllQuestions] = useState<QuestionSchema[]>([])
  const [isLoading, setIsLoading] = useState({
    form: false,
    submissions: false
  })
  const [requestState, setRequestState] = useState({ info: '', error: '' })

  const dispatch = useAppDispatch()
  const formState = useAppSelector((state) => state.form)
  const { data: globalForms, form } = formState
  const submissionState = useAppSelector((state) => state.submission)
  const { data: globalSubmissions } = submissionState
  const navigate = useNavigate()
  const { formId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedTab = searchParams.get('tab')

  const viewTabs = [
    {
      name: `${form?.isPublic ? 'responses' : 'entries'}`,
      element: (
        <SubmissionsTab
          form={form}
          submissions={submissions}
          allQuestions={allQuestions}
          requestState={requestState}
          setRequestState={setRequestState}
        />
      )
    },
    {
      name: 'questions',
      element: <QuestionsTab form={form} submissions={submissions} allQuestions={allQuestions} />
    }
  ]

  useEffect(() => {
    if (formId) {
      populateData(formId)
    }
    // eslint-disable-next-line
  }, [formId])

  useEffect(() => {
    if (formId) {
      const formSubmissions = globalSubmissions[formId]
      formSubmissions && setSubmissions(JSON.parse(JSON.stringify(formSubmissions)))
    }
  }, [formId, globalSubmissions])

  useEffect(() => {
    if (form) {
      const questions = form.sections.flatMap(({ questions }) => questions)
      setAllQuestions(questions)
    }
  }, [form])

  const populateData = async (formId: string) => {
    const existingForm = form?._id === formId ? form : globalForms.find(({ _id }) => _id === formId)

    try {
      if (existingForm) {
        dispatch(setGlobalForm(existingForm))
        const formSubmissions = globalSubmissions[existingForm._id]

        if (
          !existingForm.submissions.length ||
          existingForm.submissions.length === formSubmissions?.length
        ) {
          // Do nothing
        } else {
          setIsLoading({ ...isLoading, submissions: true })
          await dispatch(fetchSubmissions(existingForm._id)).unwrap()
        }
      } else {
        setIsLoading({ ...isLoading, form: true })
        await dispatch(fetchForm(formId)).unwrap()

        setIsLoading({ ...isLoading, submissions: true })
        await dispatch(fetchSubmissions(formId)).unwrap()
      }
    } catch (error: any) {
      const errorObj = JSON.parse(error.message)
      if (errorObj.status === 403 || errorObj.status === 404) {
        setRequestState({ ...requestState, error: 'no_access' })
      } else {
        setRequestState({
          ...requestState,
          error: 'Error encountered processing the request'
        })
      }
    } finally {
      setIsLoading({ form: false, submissions: false })
    }
  }

  const handleAddSubmission = () => {
    if (form) {
      const dirtySubmissionForm = getDirtySubmissionForm()
      const isSubmittingForm = dirtySubmissionForm?._id === form._id

      if (dirtySubmissionForm && !isSubmittingForm) {
        setRequestState({
          ...requestState,
          info: 'Submission already in progress!'
        })
        return
      }

      navigate(`/form/${form._id}/submit`, { viewTransition: true })
    }
  }

  return (
    <>
      {isLoading.form ? (
        <Spinner size='center' />
      ) : (
        <>
          <div className='main_content'>
            <NavBack />
            {requestState.info && (
              <Status
                info={requestState.info}
                reset={() => setRequestState({ ...requestState, info: '' })}
              />
            )}
            {requestState.error === 'no_access' ? (
              <Status warning='Form cannot be accessed' />
            ) : (
              <>
                {requestState.error && (
                  <Status
                    error={requestState.error}
                    reset={() => setRequestState({ ...requestState, error: '' })}
                  />
                )}
                {form && (
                  <>
                    <header>
                      <h1 className='heading-styled'>{form.name}</h1>
                      <p>{form.description}</p>
                    </header>
                    <div className={`actions_series theme-content-${form.meta.colour}`}>
                      {viewTabs.map(({ name }) => (
                        <span
                          className={`${selectedTab === name ? 'actions-selected' : ''}`}
                          onClick={() => setSearchParams({ tab: name })}
                          key={name}>
                          {capitalize(name)}
                        </span>
                      ))}
                    </div>
                    {isLoading.submissions ? (
                      <span>...</span>
                    ) : (
                      <>
                        {viewTabs
                          .filter(({ name }) => selectedTab === name)
                          .map(({ name, element }) => (
                            <Fragment key={name}>{element}</Fragment>
                          ))}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {form && !form.isPublic && (
            <div className={`footer_actions footer-sticky theme-content-${form.meta.colour}`}>
              <span
                className='material-symbols-rounded badge-rounded'
                onClick={handleAddSubmission}>
                add
              </span>
            </div>
          )}
        </>
      )}
    </>
  )
}
